import React, { useState, useEffect } from 'react';

export interface EmailTemplate {
  id: string;
  name: string;
  category: 'Festival' | 'Raffle' | 'Security' | 'Diaspora' | 'VIP';
  subject: string;
  body: string;
  updatedAt: string;
}

const TEMPLATES_STORAGE_KEY = 'uzairue_email_templates_v1';

const DEFAULT_TEMPLATES: EmailTemplate[] = [
  {
    id: 'tmpl-1',
    name: 'Diaspora Return & Welcome Invitation',
    category: 'Diaspora',
    subject: 'Official Return Invitation: ERUE Festival 2026 in Jattu-Uzairue',
    body: 'Dear [AttendeeName],\n\nThe Royal Palace and the people of Uzairue Kingdom cordially welcome you home for the ERUE Festival 2026. Your roots in [Village] await your triumphant return.\n\nKey Events:\n- Day 1: Royal Blessing & Opening (Dec 24)\n- Day 4: Grand Durbar & Cultural Re-enactment (Dec 27)\n- Day 7: Grand Finale & Mega Raffle Draw (Dec 30)\n\nKindly confirm your arrival with the Protocol Desk.',
    updatedAt: new Date().toLocaleDateString(),
  },
  {
    id: 'tmpl-2',
    name: 'Mega Raffle Ticket & SUV Win Notice',
    category: 'Raffle',
    subject: 'Raffle Ticket Confirmation & Grand Prize Draw Alert - ERUE 2026',
    body: 'Greetings [AttendeeName],\n\nYour purchase of ERUE Festival Grand Raffle tickets has been successfully recorded in the Kingdom Treasury. Ticket Serial: [TicketNo].\n\nJoin us live at the Ogieneni Palace Arena on December 19st, 2026 for the Mega Draw where an SUV Car, 10kVA Solar Systems, and University Scholarships will be won!',
    updatedAt: new Date().toLocaleDateString(),
  },
  {
    id: 'tmpl-3',
    name: 'Security & Peaceful Assembly Advisory',
    category: 'Security',
    subject: 'Joint Security Advisory for ERUE Festival 2026 Participants',
    body: 'Attention [AttendeeName],\n\nThe Joint Security Command of Uzairue Kingdom (Police, Vigilante, and FRSC) issues this safety advisory for all visitors and indigenes. Emergency Hotline: +234 803 123 4567. Enjoy a peaceful and secure celebration across all 22 villages.',
    updatedAt: new Date().toLocaleDateString(),
  },
];

export const EmailTemplateManager: React.FC<{ onSelectTemplate?: (subject: string, body: string) => void }> = ({ onSelectTemplate }) => {
  const [templates, setTemplates] = useState<EmailTemplate[]>(() => {
    try {
      const saved = localStorage.getItem(TEMPLATES_STORAGE_KEY);
      if (saved) return JSON.parse(saved);
    } catch (e) {}
    return DEFAULT_TEMPLATES;
  });

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [category, setCategory] = useState<EmailTemplate['category']>('Festival');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  useEffect(() => {
    try {
      localStorage.setItem(TEMPLATES_STORAGE_KEY, JSON.stringify(templates));
    } catch (e) {}
  }, [templates]);

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !subject.trim() || !body.trim()) {
      alert('Please fill in all template fields.');
      return;
    }

    if (currentId) {
      setTemplates(templates.map(t => t.id === currentId ? {
        ...t,
        name,
        category,
        subject,
        body,
        updatedAt: new Date().toLocaleDateString(),
      } : t));
    } else {
      const newTmpl: EmailTemplate = {
        id: `tmpl-${Date.now()}`,
        name,
        category,
        subject,
        body,
        updatedAt: new Date().toLocaleDateString(),
      };
      setTemplates([newTmpl, ...templates]);
    }

    resetForm();
  };

  const handleEdit = (tmpl: EmailTemplate) => {
    setCurrentId(tmpl.id);
    setName(tmpl.name);
    setCategory(tmpl.category);
    setSubject(tmpl.subject);
    setBody(tmpl.body);
    setIsEditing(true);
  };

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this email template?')) {
      setTemplates(templates.filter(t => t.id !== id));
    }
  };

  const resetForm = () => {
    setIsEditing(false);
    setCurrentId(null);
    setName('');
    setCategory('Festival');
    setSubject('');
    setBody('');
  };

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 md:p-8 shadow-2xl space-y-8 text-white">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-slate-800 pb-6">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-2xl">📧</span>
            <h2 className="text-xl md:text-2xl font-serif font-black text-amber-300">
              Bulk-Mailing & Notification Template Manager
            </h2>
            <span className="bg-amber-400 text-slate-950 text-[10px] px-2.5 py-0.5 rounded-full font-extrabold uppercase">
              Reusable Templates
            </span>
          </div>
          <p className="text-xs text-slate-300 mt-1">
            Create, store, and apply professional email & notification templates for festival announcements, raffle alerts, and diaspora welcomes.
          </p>
        </div>

        <button
          onClick={() => { resetForm(); setIsEditing(!isEditing); }}
          className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-black px-4 py-2.5 rounded-xl text-xs uppercase tracking-wider transition-all cursor-pointer flex items-center gap-1.5 shadow-md"
        >
          <span>{isEditing ? '✕ Cancel' : '➕ Create New Template'}</span>
        </button>
      </div>

      {isEditing && (
        <form onSubmit={handleSave} className="bg-slate-950 p-6 rounded-2xl border border-amber-500/40 space-y-4">
          <h3 className="text-sm font-bold text-amber-300 font-serif">
            {currentId ? 'Edit Email Template' : 'Create New Reusable Template'}
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-bold text-slate-300 mb-1">Template Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. VIP Reception Notice"
                className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:border-amber-400 outline-none"
                required
              />
            </div>

            <div>
              <label className="block text-[11px] font-bold text-slate-300 mb-1">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as any)}
                className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:border-amber-400 outline-none"
              >
                <option value="Festival">Festival</option>
                <option value="Raffle">Raffle</option>
                <option value="Security">Security</option>
                <option value="Diaspora">Diaspora</option>
                <option value="VIP">VIP</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-bold text-slate-300 mb-1">Email Subject Line</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. Official Update regarding ERUE Festival 2026"
              className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:border-amber-400 outline-none"
              required
            />
          </div>

          <div>
            <label className="block text-[11px] font-bold text-slate-300 mb-1">Email Body Content (Supports [AttendeeName], [Village], etc.)</label>
            <textarea
              rows={5}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Dear [AttendeeName], ..."
              className="w-full bg-slate-900 border border-slate-700 rounded-xl p-3 text-xs text-white focus:border-amber-400 outline-none font-mono"
              required
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={resetForm}
              className="px-4 py-2 bg-slate-800 text-slate-300 rounded-xl text-xs font-bold hover:bg-slate-700 cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-5 py-2 bg-[#0F5132] hover:bg-[#0b3d26] text-amber-300 rounded-xl text-xs font-bold shadow-md cursor-pointer"
            >
              💾 Save Template
            </button>
          </div>
        </form>
      )}

      {/* Templates List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map((tmpl) => (
          <div key={tmpl.id} className="bg-slate-950 p-6 rounded-2xl border border-slate-800 space-y-3 flex flex-col justify-between">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase bg-amber-500/20 text-amber-300 border border-amber-500/30">
                  {tmpl.category}
                </span>
                <span className="text-[10px] text-slate-400 font-mono">Updated: {tmpl.updatedAt}</span>
              </div>
              <h3 className="font-serif font-bold text-sm text-white">{tmpl.name}</h3>
              <p className="text-xs font-semibold text-amber-400 truncate">Subject: {tmpl.subject}</p>
              <p className="text-xs text-slate-300 line-clamp-3 font-sans whitespace-pre-line">{tmpl.body}</p>
            </div>

            <div className="pt-4 border-t border-slate-800 flex items-center justify-between gap-2">
              {onSelectTemplate && (
                <button
                  onClick={() => onSelectTemplate(tmpl.subject, tmpl.body)}
                  className="px-3 py-1.5 bg-emerald-700 hover:bg-emerald-600 text-white rounded-xl text-xs font-bold transition-all cursor-pointer"
                >
                  📥 Load to Broadcast
                </button>
              )}
              <div className="flex items-center gap-2 ml-auto">
                <button
                  onClick={() => handleEdit(tmpl)}
                  className="p-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg text-xs cursor-pointer"
                  title="Edit Template"
                >
                  ✏️
                </button>
                <button
                  onClick={() => handleDelete(tmpl.id)}
                  className="p-1.5 bg-red-950/60 hover:bg-red-900 text-red-300 rounded-lg text-xs cursor-pointer"
                  title="Delete Template"
                >
                  🗑️
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
